import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Mail,
  FilePenLine,
  Film,
  Shield,
  LogOut,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/Logo";
import { adminApi, getAdminToken } from "../../lib/adminApi";

const links = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, feature: "overview" },
  { to: "/admin/visitors", label: "Visitors", icon: Users, feature: "visitors" },
  { to: "/admin/contacts", label: "Messages", icon: Mail, badge: "messages", feature: "contacts" },
  { to: "/admin/content", label: "Website", icon: FilePenLine, feature: "website" },
  { to: "/admin/videos", label: "Videos", icon: Film, feature: "videos" },
  { to: "/admin/admins", label: "Admins", icon: Shield, superadmin: true },
];

function UnreadBadge({ count, inverted }) {
  if (!count) return null;
  return (
    <span
      className={`ml-auto min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-semibold grid place-items-center ${
        inverted ? "bg-white text-[#EC4899]" : "bg-[#EC4899] text-white"
      }`}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

export default function AdminLayout() {
  const { admin, isSuperadmin, can, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const navLinks = links.filter((l) => (l.superadmin ? isSuperadmin : can(l.feature, "view")));

  const refreshUnread = useCallback(() => {
    const token = getAdminToken();
    if (!token || !can("contacts", "view")) return;
    adminApi
      .visitStats(token)
      .then((stats) => setUnread(Number(stats?.unreadContacts) || 0))
      .catch(() => {});
  }, [can]);

  useEffect(() => {
    refreshUnread();
    const timer = setInterval(refreshUnread, 20000);
    const onRefresh = () => refreshUnread();
    window.addEventListener("focus", onRefresh);
    window.addEventListener("cf-unread-refresh", onRefresh);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onRefresh);
      window.removeEventListener("cf-unread-refresh", onRefresh);
    };
  }, [refreshUnread, location.pathname]);

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F7F4EF] text-black flex">
      <aside className="hidden md:flex w-64 flex-col bg-[#0A0A0A] text-white">
        <div className="px-6 py-7 border-b border-white/10 flex items-center gap-3">
          <Logo className="h-11 w-11" />
          <div>
            <div className="font-display text-xl leading-none">Creative Flare</div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#EC4899] mt-2">Admin</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {navLinks.map((l) => {
              const Icon = l.icon;
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  aria-label={
                    l.badge === "messages" && unread
                      ? `${l.label}, ${unread} new`
                      : l.label
                  }
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-colors ${
                      isActive ? "bg-[#EC4899] text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon size={16} />
                      <span className="flex-1">{l.label}</span>
                      {l.badge === "messages" && (
                        <UnreadBadge count={unread} inverted={isActive} />
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
        </nav>
        <div className="px-6 py-6 border-t border-white/10">
          <div className="text-sm text-white/80">{admin?.username}</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#EC4899] mt-1">{admin?.role}</div>
          <button onClick={onLogout} className="mt-4 inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="md:hidden flex items-center justify-between px-5 py-4 bg-[#0A0A0A] text-white">
          <span className="font-display text-lg flex items-center gap-2">
            <Logo className="h-8 w-8" /> CF Admin
          </span>
          <button onClick={onLogout} className="text-sm text-white/70">Sign out</button>
        </header>
        <nav className="md:hidden flex gap-2 overflow-x-auto px-4 py-3 bg-white border-b border-black/5">
          {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${
                    isActive ? "bg-[#EC4899] text-white" : "bg-black/5 text-black/70"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {l.badge === "messages" && unread > 0 && (
                      <span
                        className={`min-w-[1.15rem] h-[1.15rem] px-1 rounded-full text-[10px] font-semibold grid place-items-center ${
                          isActive ? "bg-white text-[#EC4899]" : "bg-[#EC4899] text-white"
                        }`}
                      >
                        {unread > 99 ? "99+" : unread}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
        </nav>
        <main className="p-5 md:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
